import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { extractApiErrorMessage, isNetworkError } from '../utils/error-message.utils';
import { isErrorHandledByInterceptor } from '../utils/interceptor-handled.utils';
import { isObject } from '../utils/object.utils';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  public readonly DEFAULT_DURATION = 15000;

  constructor(private readonly snackBar: MatSnackBar) {}

  /**
   * Shows message in snackbar
   * @param message Success message to display in snackbar to notify user
   */
  showMessage(message: string, duration?: number): void {
    this.snackBar.open(message, 'OK', {
      duration: duration ?? this.DEFAULT_DURATION,
      panelClass: ['notification-neutral'],
    });
  }

  /**
   * Shows error in snackbar
   * @param message Error message to display in snackbar to notify user
   * @param duration How long should be displayed
   * @returns MatSnackBarRef The instance of the component making up the content of the snack bar.
   */
  showError(message: string, duration?: number): MatSnackBarRef<unknown> {
    return this.snackBar.open(message, 'CLOSE', {
      duration: duration ?? this.DEFAULT_DURATION,
      panelClass: ['notification-error'],
    });
  }

  showErrorOrMessage(
    error: unknown,
    fallbackMessage: string,
    duration?: number,
  ): MatSnackBarRef<unknown> | null {
    if (isNetworkError(error) || isErrorHandledByInterceptor(error)) return null;
    const msg =
      extractApiErrorMessage(error) ??
      fallbackMessage ??
      'Error occurred, please contact administrator!';
    return this.showError(msg, duration);
  }

  showResponseMessage(response: unknown, message: string, duration?: number): void {
    if (!isObject(response)) {
      this.showError('Invalid server response');
      return;
    }

    const res = response as Record<string, unknown>;

    let panelClass = 'notification-neutral';
    let msg = message;

    if (res['isFailed']) {
      panelClass = 'notification-error';
      msg = `Failed: ${(res['message'] as string) ?? 'Operation failed!'}`;
    } else if (res['isSuccessed']) {
      panelClass = 'notification-success';
      msg = `Success: ${(res['message'] as string) ?? 'Operation succeeded!'}`;
    } else if (res['isApproveNeeded']) {
      panelClass = 'notification-warning';
      msg = `Warning: ${(res['message'] as string) ?? 'Approval needed!'}`;
    }

    this.snackBar.open(msg, 'CLOSE', {
      duration: duration ?? this.DEFAULT_DURATION,
      panelClass: [panelClass],
    });
  }
}
